// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { login as loginService, fetchUserData } from '../services/auth';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    usssaJwt: null,
  });

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    const usssaToken = localStorage.getItem('usssaToken');

    if (token) {
      // If we have a DRF token, try to fetch user data
      try {
        const response = await fetchUserData();
        const userData = response.data;

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: userData,
          usssaJwt: userData.usssa_jwt || usssaToken || null,
        });

        // Store USSSA JWT if present in user data
        if (userData.usssa_jwt) {
          localStorage.setItem('usssaToken', userData.usssa_jwt);
        }

        console.log('checkAuth - authenticated with user data');
      } catch (error) {
        // If fetching user data fails, still consider authenticated but without user data
        console.warn('Failed to fetch user data on init:', error);
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: null,
          usssaJwt: usssaToken || null,
        });
      }
    } else {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        usssaJwt: null,
      });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (credentials) => {
    try {
      // Step 1: Login and get DRF token
      const response = await loginService(credentials);
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        console.log('Login successful - DRF token set');

        // Step 2: Fetch user data (includes USSSA JWT for rnd user)
        try {
          const userResponse = await fetchUserData();
          const userData = userResponse.data;

          // Step 3: Store USSSA JWT if present
          if (userData.usssa_jwt) {
            localStorage.setItem('usssaToken', userData.usssa_jwt);
            console.log('USSSA JWT stored:', userData.usssa_jwt);
          }

          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: userData,
            usssaJwt: userData.usssa_jwt || null,
          });
        } catch (userError) {
          console.error('Failed to fetch user data:', userError);
          // Still set authenticated state even if user fetch fails
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: null,
            usssaJwt: null,
          });
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('usssaToken');
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      usssaJwt: null,
    });
    console.log('Logged out - tokens removed');
  }, []);

  const value = useMemo(
    () => ({ ...authState, login, logout, checkAuth }),
    [authState, checkAuth, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};